/**
 * Validates returnUrl to prevent open redirect vulnerabilities.
 * Returns a safe relative URL within the app (always starting with '/'), or null.
 *
 * Rules:
 * - Only relative paths are allowed (no absolute http(s) URLs).
 * - No protocol/scheme tokens (like "javascript:", including percent-encoded forms).
 * - No backtracking segments ("..") allowed.
 * - No double slashes "//" anywhere.
 * - Backslashes are normalized to '/'.
 * - Preserves query string and hash if present.
 */
export function validateReturnUrl(returnUrl) {
  if (!returnUrl || typeof returnUrl !== 'string') {
    return null;
  }

  // Decode once to handle normal encodings; if decoding fails, reject
  let decoded;
  try {
    decoded = decodeURIComponent(returnUrl);
  } catch {
    return null;
  }

  // Trim and normalize backslashes -> forward slashes
  decoded = decoded.trim().replace(/\\/g, '/');
  if (decoded.length === 0) return null;

  // Separate path from query/hash. Keep suffix to append later.
  let qm = decoded.indexOf('?');
  let hash = decoded.indexOf('#');
  let splitIdx;
  if (qm === -1 && hash === -1) splitIdx = -1;
  else if (qm === -1) splitIdx = hash;
  else if (hash === -1) splitIdx = qm;
  else splitIdx = Math.min(qm, hash);

  let pathPart = splitIdx === -1 ? decoded : decoded.slice(0, splitIdx);
  let suffix = splitIdx === -1 ? '' : decoded.slice(splitIdx);

  // Lowercase version for protocol checks
  let lowerDecoded = decoded.toLowerCase();

  // Detect protocol/scheme patterns, including percent-encoded colon (%3a)
  let normalizedForProto = lowerDecoded.replace(/%3a/g, ':');
  if (/^[a-z][a-z0-9+.-]*:/i.test(normalizedForProto)) {
    // Starts with a scheme -> reject (covers double-encoded schemes as well)
    return null;
  }

  // Reject any occurrence of protocol-like token anywhere (e.g. "javascript:")
  let dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:'];
  for (let i = 0; i < dangerousSchemes.length; i++) {
    if (normalizedForProto.indexOf(dangerousSchemes[i]) !== -1) return null;
  }

  // Must be relative: disallow leading protocol-relative '//' and absolute schemes
  if (pathPart.indexOf('//') === 0) return null;
  // Disallow absolute http(s) etc: if pathPart matches /^https?:\/\// -> reject
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(pathPart)) return null;

  // No double slashes allowed anywhere in pathPart
  if (pathPart.indexOf('//') !== -1) return null;

  // Split into segments and reject any backtracking '..' segments
  let segments = pathPart.split('/');
  for (let si = 0; si < segments.length; si++) {
    let seg = segments[si];
    if (seg === '..') return null; // backtracking not allowed
    // Reject segments containing NUL or control characters
    for (let j = 0; j < seg.length; j++) {
      if (seg.charCodeAt(j) < 32) return null;
    }
  }

  // Remove leading ./ segments
  let cleanedSegments = segments.filter(function (s) {
    return s !== '' && s !== '.';
  });

  // Rebuild normalized path
  let normalizedPath = '/' + cleanedSegments.join('/');

  // Final sanity: path must start with '/' and not contain suspicious sequences
  if (normalizedPath.indexOf('/') !== 0) return null;
  if (normalizedPath.indexOf('//') !== -1) return null;

  return normalizedPath + suffix;
}

/**
 * Convert any passed URL (absolute or relative) into a safe relative URL within the app,
 * or return null if it cannot be represented as a safe relative URL.
 *
 * Rules:
 * - If the input is a relative safe path, normalize and return it (same as validateReturnUrl).
 * - If the input is an absolute http(s) URL and its host matches the current host, return its
 *   pathname + search + hash normalized to a safe relative path.
 * - Otherwise return null.
 */
export function convertToRelativeUrl(input) {
  if (!input || typeof input !== 'string') return null;

  // Decode once; reject invalid encodings
  let decoded;
  try {
    decoded = decodeURIComponent(input);
  } catch {
    return null;
  }

  // Normalize backslashes and trim
  decoded = decoded.trim().replace(/\\/g, '/');
  if (decoded.length === 0) return null;

  let lower = decoded.toLowerCase();
  let normalizedForProto = lower.replace(/%3a/g, ':');

  // If there's any dangerous scheme token anywhere, reject
  let dangerousSchemes2 = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:'];
  for (let k = 0; k < dangerousSchemes2.length; k++) {
    if (normalizedForProto.indexOf(dangerousSchemes2[k]) !== -1) return null;
  }

  // Protocol-relative URLs are rejected
  if (decoded.indexOf('//') === 0) return null;

  // If it's an absolute-looking URL (has scheme://), try to parse and ensure it's same-host
  let absoluteSchemeMatch = /^([a-z][a-z0-9+.-]*):\/\//i.test(decoded);
  if (absoluteSchemeMatch) {
    // Only allow http/https absolute URLs
    if (!/^https?:\/\//i.test(decoded)) return null;
    try {
      let u = new URL(decoded);
      // derive current host from window.location.href (works in tests where location may be mocked)
      let currentHost;
      try {
        currentHost = new URL(window.location.href).host;
      } catch {
        if (typeof window !== 'undefined' && window.location && window.location.host) {
          currentHost = window.location.host;
        } else {
          currentHost = '';
        }
      }
      if (u.host !== currentHost) return null;

      // Build relative path (pathname + search + hash), normalize repeating slashes
      let path = (u.pathname || '/') + (u.search || '') + (u.hash || '');
      // Now convert path to a safe relative path using validateReturnUrl logic
      return validateReturnUrl(path);
    } catch {
      return null;
    }
  }

  // Otherwise attempt to validate as a relative path
  return validateReturnUrl(decoded);
}

/**
 * Convert an absolute URL into a relative URL using a supplied base which may be:
 * - a host (example.com),
 * - a host with path (example.com/folder),
 * - a full origin (https://example.com or https://example.com/folder),
 * - or a path on the current host (/folder or folder).
 *
 * The function returns a normalized, safe relative path (always starting with '/')
 * that represents the absolute URL relative to the supplied base. Returns null when
 * the absolute URL cannot be represented relative to the base or is unsafe.
 *
 * Contract:
 * - Inputs: absoluteUrl (must be an absolute URL string), base (string as described).
 * - Output: a safe relative URL (starting with '/') or null on validation failure.
 */
export function convertAbsoluteToRelativeWithBase(absoluteUrl, base) {
  if (!absoluteUrl || typeof absoluteUrl !== 'string') return null;
  if (!base || typeof base !== 'string') return null;

  absoluteUrl = decodeURIComponent(absoluteUrl).trim();

  // Parse the absolute URL first
  let abs;
  try {
    abs = new URL(absoluteUrl);
  } catch {
    return null; // not an absolute URL we can parse
  }

  // Normalize base and attempt to construct a URL object for matching
  let baseUrl;
  let baseHasHost = false;
  let trimmedBase = base.trim();
  if (trimmedBase.length === 0) return null;

  // Helper to get current host (fall back to abs.host if window not available)
  let currentHost;
  try {
    currentHost = new URL(window.location.href).host;
  } catch {
    if (typeof window !== 'undefined' && window.location && window.location.host) {
      currentHost = window.location.host;
    } else {
      currentHost = abs.host;
    }
  }

  try {
    if (trimmedBase.charAt(0) === '/') {
      // Path-only base on current host
      baseUrl = new URL(
        trimmedBase,
        typeof window !== 'undefined' && window.location && window.location.href
          ? window.location.href
          : 'https://' + currentHost + '/',
      );
      baseHasHost = false;
    } else {
      // Heuristic: if the base contains '.' or ':' or a '/', treat it as host-like (try to parse as host[/path])
      // Otherwise treat it as a path-only base (e.g., 'folder') on the current host.
      if (trimmedBase.indexOf('/') !== -1 || trimmedBase.indexOf('.') !== -1 || trimmedBase.indexOf(':') !== -1) {
        // Try parsing as full URL first
        try {
          baseUrl = new URL(trimmedBase);
          baseHasHost = true;
        } catch {
          // Try adding https:// for host-like bases (example.com or example.com/folder)
          try {
            baseUrl = new URL('https://' + trimmedBase);
            baseHasHost = true;
          } catch {
            // Can't parse as host-like base
            return null;
          }
        }
      } else {
        // Treat as path relative to current host (e.g., "folder")
        baseUrl = new URL(
          '/' + trimmedBase.replace(/^\/+/, ''),
          typeof window !== 'undefined' && window.location && window.location.href
            ? window.location.href
            : 'https://' + currentHost + '/',
        );
        baseHasHost = false;
      }
    }
  } catch {
    return null;
  }

  // Ensure hosts match: if base specified a host, require it to match abs.host.
  if (baseHasHost) {
    if (baseUrl.host !== abs.host) return null;
  } else {
    // base is path-only: require absolute url to be same host as current host
    if (abs.host !== currentHost) return null;
  }

  // Normalize base path for prefix matching
  let basePath = baseUrl.pathname || '/';
  // Remove trailing slashes except if root
  if (basePath.length > 1) basePath = basePath.replace(/\/+$/, '');

  // Absolute path to match against
  let absPath = abs.pathname || '/';

  // Check that absPath is within the basePath
  if (basePath === '/') {
    // always ok
  } else {
    if (absPath === basePath) {
      // exact match -> remaining is '/'
    } else if (absPath.indexOf(basePath + '/') !== 0) {
      return null; // not under the base path
    }
  }

  // Compute remaining path relative to the base
  let remaining;
  if (basePath === '/') {
    remaining = absPath;
  } else if (absPath === basePath) {
    remaining = '/';
  } else {
    remaining = absPath.slice(basePath.length);
    if (remaining.charAt(0) !== '/') remaining = '/' + remaining;
  }

  // Append search and hash
  let candidate = (remaining || '/') + (abs.search || '') + (abs.hash || '');

  // Validate resulting candidate using existing validator to ensure safety
  return validateReturnUrl(candidate);
}
